import { Board, MaterialInstance } from "../Materials";
import { isBoard } from "../board-helpers";
import { makeMaterial } from "../material-helpers";
import { MachineType } from "../Machine";
import { GENERATED_COLLISION_SHAPES } from "../machine-collision-boxes.generated";

/**
 * The flattening machine. A jointer makes one face flat (the reference the
 * planer needs) and one edge straight-and-square (referenced off a flat
 * face against the fence). It can't make anything *parallel* — that's the
 * planer's job for faces and the table saw's for edges — so each axis only
 * ever goes 0 → 1 here.
 */
export const jointer: MachineType = {
  id: "jointer",
  name: "Jointer",
  description:
    "Flattens one reference face and straightens one edge. The first machine in the milling chain.",
  // A benchtop jointer: 16" wide with 40" of bed, so a 2×3-ft footprint
  // with the tables running the long way (the beds overhang the footprint
  // an inch or two each end — see the measured collision box).
  cellsOccupied: [
    [0, -1],
    [0, 0],
    [0, 1],
    [1, -1],
    [1, 0],
    [1, 1],
  ],
  collisionShapes: GENERATED_COLLISION_SHAPES.jointer,
  // Infeed and outfeed: boards travel the long way across the tables
  freeCellsNeeded: [
    [0, 2],
    [1, 2],
    [0, -2],
    [1, -2],
  ],
  operationPosition: [0, 2],
  outputPosition: [0, -2],
  cost: 600,
  materialStorage: 0,
  toolSlots: 1,
  // One board on the beds at a time. There is no face/edge mode — how the
  // stock arrives decides (a rough board can only take a face pass; a
  // face-jointed one gets its edge).
  inputSpaces: 1,
  directFeed: true,
  feedsThrough: true,
  // Small enough to mount on a worktable cell instead of the floor
  benchtop: true,
  powerSwitch: true,
  corded: true,
  operations: [
    {
      id: "jointFace",
      requiredSkill: "basicMilling",
      name: "Joint Face",
      duration: 10,
      dustOutput: 2.4,
      getInputMaterials: () => [
        { type: ["board"], jointedFaces: [0], quantity: 1 },
      ],
      // Only reached when no jointer op takes the board — a flat-faced
      // board with a rough edge feeds the edge pass instead, so landing
      // here means the stock is already flat AND straight
      explainRejection: (material) =>
        isBoard(material) &&
        material.jointedFaces >= 1 &&
        material.jointedEdges >= 1
          ? "Flat face, straight edge — nothing left to joint. Parallel is the planer's job for faces and the table saw's for edges."
          : null,
      output: (materials: ReadonlyArray<MaterialInstance>) => {
        const inputBoard = materials[0];
        if (!isBoard(inputBoard)) {
          throw new Error("Input material is not a board");
        }
        // One flat face; the rest of the board is as rough as it came
        return {
          inputs: [],
          outputs: [makeMaterial<Board>({ ...inputBoard, jointedFaces: 1 })],
        };
      },
    },
    {
      id: "jointEdge",
      requiredSkill: "basicMilling",
      name: "Joint Edge",
      duration: 8,
      dustOutput: 1.6,
      getInputMaterials: () => [
        {
          type: ["board"],
          // Edge squareness references a flat face against the fence
          jointedFaces: [1, 2],
          jointedEdges: [0],
          quantity: 1,
        },
      ],
      output: (materials: ReadonlyArray<MaterialInstance>) => {
        const inputBoard = materials[0];
        if (!isBoard(inputBoard)) {
          throw new Error("Input material is not a board");
        }
        return {
          inputs: [],
          outputs: [makeMaterial<Board>({ ...inputBoard, jointedEdges: 1 })],
        };
      },
    },
  ],
};
